import React from "react";
import { useNavigate } from "react-router-dom";
import {
  IconMenu2,
  IconX,
  IconUserCog,
  IconShieldLock,
  IconListDetails,
  IconPower,
} from "@tabler/icons-react";
import { useAuth } from "../../Auth/AuthProvider";
import ProfilePic from "../ProfilePic/ProfilePic";
import NavLink from "../NavLink";
import { routes } from "../../routes";
import { Button } from "@/Components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetFooter,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/Components/ui/sheet";

/**
 * @param {{ open: boolean, onOpenChange: (open: boolean) => void }} props
 */
export default function MobileMenu({ open, onOpenChange }) {
  const { displayName, email, logout } = useAuth();
  const navigate = useNavigate();

  const close = () => onOpenChange(false);

  const handleLogout = () => {
    close();
    logout().then(() => {
      navigate(routes.home.path);
    });
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetTrigger
        render={
          <Button
            variant="ghost"
            size="icon-lg"
            className="size-11 text-mauve-100 hover:bg-mauve-800 hover:text-white md:hidden [&_svg:not([class*='size-'])]:size-6"
            aria-label={open ? "Close navigation" : "Open navigation"}
          >
            {open ? <IconX /> : <IconMenu2 />}
          </Button>
        }
      />

      <SheetContent
        side="right"
        showCloseButton={false}
        className="gap-0 border-mauve-800 bg-mauve-900 p-0 text-mauve-50"
      >
        <SheetHeader className="border-b border-mauve-800 px-5 py-4 flex flex-row justify-between items-center">
          <SheetTitle className="text-mauve-50">Menu</SheetTitle>
          <SheetDescription className="sr-only">
            Primary navigation and account actions
          </SheetDescription>
          <Button
            variant="ghost"
            size="icon-lg"
            className="size-9 text-mauve-200 hover:bg-mauve-800 hover:text-white [&_svg:not([class*='size-'])]:size-6"
            aria-label="Close navigation"
            onClick={close}
          >
            <IconX />
          </Button>
        </SheetHeader>

        <div className="flex flex-1 flex-col overflow-y-auto">
          <div className="flex flex-col items-center gap-2 border-b border-mauve-800 px-5 py-6">
            <ProfilePic size={72} />
            <div className="text-center">
              <p className="text-sm font-medium text-white">
                {displayName || email}
              </p>
              {displayName && email ? (
                <p className="text-xs text-mauve-400">{email}</p>
              ) : null}
            </div>
          </div>

          <nav className="flex flex-col gap-1 p-3">
            <NavLink
              to={routes.dashboard.path}
              onClick={close}
              className="rounded-sm px-3 py-2.5 text-sm text-mauve-200 transition hover:bg-mauve-800 hover:text-white"
              activeClassName="bg-mauve-800 text-white"
            >
              Dashboard
            </NavLink>

            <div className="my-2 border-t border-mauve-800" />

            <NavLink
              to={routes.accountSettings.path}
              onClick={close}
              className="inline-flex items-center gap-2 rounded-sm px-3 py-2.5 text-sm text-mauve-200 transition hover:bg-mauve-800 hover:text-white"
              activeClassName="bg-mauve-800 text-white"
            >
              <IconUserCog className="size-4" />
              Account Settings
            </NavLink>

            <NavLink
              to={routes.privacyPolicy.path}
              onClick={close}
              className="inline-flex items-center gap-2 rounded-sm px-3 py-2.5 text-sm text-mauve-200 transition hover:bg-mauve-800 hover:text-white"
              activeClassName="bg-mauve-800 text-white"
            >
              <IconShieldLock className="size-4" />
              Privacy Policy
            </NavLink>

            <NavLink
              to={routes.termsOfUse.path}
              onClick={close}
              className="inline-flex items-center gap-2 rounded-sm px-3 py-2.5 text-sm text-mauve-200 transition hover:bg-mauve-800 hover:text-white"
              activeClassName="bg-mauve-800 text-white"
            >
              <IconListDetails className="size-4" />
              Terms of Use
            </NavLink>
          </nav>
        </div>

        <SheetFooter className="border-t border-mauve-800 p-3">
          <Button
            variant="ghost"
            className="justify-start gap-2 text-mauve-200 hover:bg-mauve-800 hover:text-white"
            onClick={handleLogout}
          >
            <IconPower className="size-4" />
            Sign out
          </Button>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
}
